import style from "./AlAmer.module.scss";
import { useState, useEffect } from "react";
// Mui
import LinearProgress from "@mui/material/LinearProgress";
import { DataGrid } from "@mui/x-data-grid";
import Chip from "@mui/material/Chip";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
// API
import useGetInstallmentRequestsApi from "../../API/useGetInstallmentRequestsApi";
import useEditInstallmentStatusApi from "../../API/useEditInstallmentStatusApi";
// import { Directions } from "@mui/icons-material";

export default function AlAmer() {
  const { data, isPending, fetchStatus } = useGetInstallmentRequestsApi();
  const updateStatusMutation = useEditInstallmentStatusApi();

  const [rows, setRows] = useState([]);

  useEffect(() => {
    if (data) {
      const formatted = (Array.isArray(data) ? data : data.data)?.map((item, index, array) => ({
        id: item.id,
        index: array.length - index,
        full_name: item.full_name,
        phone: item.phone,
        nationality: item.nationality,
        monthly_salary: item.monthly_salary,
        monthly_obligations: item.monthly_obligations,
        service_duration: item.service_duration,
        has_financial_default: item.has_financial_default,
        status: item.status,
        notes: item.notes,
        date: item.created_at,

        order_id: item.order_id,
        requested_amount: item.requested_amount,
        installment_months: item.installment_months,
        monthly_installment: item.monthly_installment,
        total_amount: item.total_amount,

        whatsapp_clicked: item.whatsapp_clicked,

        has_suspension_of_services: item.has_suspension_of_services, // Added mapping
        requested_device: item.requested_device, // Added mapping
      }));
      setRows(formatted || []);
    }
  }, [data]);

  const formatPhoneForWhatsApp = (phone) => {
    if (!phone) return null;
    const cleaned = phone.replace(/\D/g, "");
    let formatted = cleaned;
    if (formatted.startsWith("0")) formatted = "966" + formatted.substring(1);
    if (formatted.length === 9) formatted = "966" + formatted;
    return formatted;
  };

  const handleStatusChange = async (id, currentStatus) => {
    const nextStatus = currentStatus === "pending" ? "approved" : currentStatus === "approved" ? "rejected" : "pending";

    setRows((prev) => prev.map((row) => (row.id === id ? { ...row, status: nextStatus } : row)));

    try {
      await updateStatusMutation.mutateAsync({ id, status: nextStatus });
    } catch (error) {
      console.error(error);
      setRows((prev) => prev.map((row) => (row.id === id ? { ...row, status: currentStatus } : row)));
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "—";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB") + "  " + date.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", hour12: true });
  };

  const statusColor = (status) => {
    if (status === "approved") return { backgroundColor: "#2e7d32", color: "#fff" };
    if (status === "rejected") return { backgroundColor: "#d32f2f", color: "#fff" };
    return { backgroundColor: "#ed6c02", color: "#fff" };
  };

  const statusLabel = (status) => {
    if (status === "approved") return "مقبول";
    if (status === "rejected") return "مرفوض";
    return "قيد المراجعة";
  };

  const columns = [
    {
      field: "eligible",
      headerName: "مؤهل",
      flex: 1,
      minWidth: 66,
      sortable: false,
      headerAlign: "center",
      align: "center",
      disableColumnMenu: true,
      renderCell: (params) => {
        const salary = parseFloat(params.row.monthly_salary);
        const obligations = parseFloat(params.row.monthly_obligations);
        const ratio = salary > 0 ? (obligations / salary) * 100 : Infinity;

        const isEligible =
          params.row.nationality === "citizen" &&
          params.row.service_duration === "more" &&
          !params.row.has_financial_default &&
          !params.row.has_suspension_of_services &&
          ratio < 45;

        return isEligible ? <CheckCircleIcon style={{ color: "#2e7d32" }} /> : <CancelIcon style={{ color: "#d32f2f" }} />;
      },
    },
    {
      field: "whatsapp_clicked",
      headerName: "واتساب",
      flex: 1,
      minWidth: 75,
      sortable: false,
      headerAlign: "center",
      align: "center",
      disableColumnMenu: true,
      renderCell: (params) => <span style={{ color: params.value ? "#2e7d32" : "#9e9e9e", fontWeight: "bold" }}>{params.value ? "تم" : "—"}</span>,
    },
    {
      field: "order_id",
      headerName: "رقم الطلب",
      flex: 1,
      minWidth: 100,
      sortable: false,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => (
        <span
          style={{
            fontWeight: "bold",
            color: "#00274D",
            direction: "ltr",
          }}
        >
          {params.value || "—"}
        </span>
      ),
    },
    {
      field: "obligations_ratio",
      headerName: "نسبة الالتزامات",
      flex: 1,
      minWidth: 130,
      sortable: false,
      headerAlign: "center",
      align: "center",
      disableColumnMenu: true,

      renderCell: (params) => {
        const salary = parseFloat(params.row.monthly_salary);
        const obligations = parseFloat(params.row.monthly_obligations);

        if (!salary || salary === 0) {
          return (
            <Chip
              label="—"
              size="small"
              style={{
                backgroundColor: "#9e9e9e",
                color: "#fff",
                fontWeight: "bold",
                minWidth: "90px",
              }}
            />
          );
        }

        const ratio = (obligations / salary) * 100;

        let chipStyle = {};

        if (ratio <= 45) {
          chipStyle = {
            backgroundColor: "#2e7d32",
            color: "#fff",
          };
        } else {
          chipStyle = {
            backgroundColor: "#d32f2f",
            color: "#fff",
          };
        }

        return (
          <Chip
            label={`${ratio.toFixed(1)}%`}
            size="small"
            style={{
              ...chipStyle,
              fontWeight: "bold",
              fontSize: "12px",
              minWidth: "90px",
            }}
          />
        );
      },
    },
    {
      field: "phone",
      headerName: "الجوال",
      flex: 1,
      minWidth: 130,
      sortable: false,
      headerAlign: "center",
      align: "center",

      renderCell: (params) => {
        const wa = formatPhoneForWhatsApp(params.value);
        return (
          <a
            href={`https://wa.me/${wa}`}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              color: "#25D366",
              textDecoration: "none",
              fontWeight: "bold",
              padding: "3px 8px",
              borderRadius: "4px",
              backgroundColor: "rgba(37,211,102,0.1)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {params.value}
          </a>
        );
      },
    },
    {
      field: "full_name",
      headerName: "الاسم",
      flex: 1,
      minWidth: 160,
      sortable: false,
      headerAlign: "center",
      align: "center",
    },

    {
      field: "nationality",
      headerName: "الجنسية",
      flex: 1,
      minWidth: 100,
      sortable: false,
      headerAlign: "center",
      align: "center",
      //   renderCell: (params) => (params.value === "citizen" ? "مواطن" : "مقيم"),
      renderCell: (params) => (
        <span style={{ color: params.value === "citizen" ? "#2e7d32" : "#d32f2f", fontWeight: "bold" }}>{params.value === "citizen" ? "مواطن" : "مقيم"}</span>
      ),
    },
    {
      field: "monthly_salary",
      headerName: "الراتب",
      flex: 1,
      minWidth: 110,
      sortable: false,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => `${Number(params.value).toLocaleString()}`,
    },
    {
      field: "monthly_obligations",
      headerName: "الالتزامات",
      flex: 1,
      minWidth: 120,
      sortable: false,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => `${Number(params.value).toLocaleString()}`,
    },
    {
      field: "service_duration",
      headerName: "مدة الخدمة",
      flex: 1,
      minWidth: 120,
      sortable: false,
      headerAlign: "center",
      align: "center",
      //   renderCell: (params) => (params.value === "less" ? "أقل من 3 شهور" : "أكثر من 3 شهور"),
      renderCell: (params) => (
        <span style={{ color: params.value === "less" ? "#d32f2f" : "#2e7d32", fontWeight: "bold" }}>{params.value === "less" ? "أقل من 3 شهور" : "أكثر من 3 شهور"}</span>
      ),
    },
    {
      field: "has_financial_default",
      headerName: "تعثر مالي",
      flex: 1,
      minWidth: 100,
      sortable: false,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => <span style={{ color: params.value ? "#d32f2f" : "#2e7d32", fontWeight: "bold" }}>{params.value ? "نعم" : "لا"}</span>,
    },
    {
      field: "has_suspension_of_services",
      headerName: "إيقاف خدمات",
      flex: 1,
      minWidth: 110,
      sortable: false,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => <span style={{ color: params.value ? "#d32f2f" : "#2e7d32", fontWeight: "bold" }}>{params.value ? "نعم" : "لا"}</span>,
    },
    {
      field: "requested_device",
      headerName: "الجهاز المطلوب",
      flex: 1,
      minWidth: 190,
      sortable: false,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => <span style={{ fontWeight: "bold", color: "#333" }}>{params.value || "—"}</span>,
    },
    {
      field: "requested_amount",
      headerName: "المبلغ المختار",
      flex: 1,
      minWidth: 140,
      sortable: false,
      headerAlign: "center",
      align: "center",
      // renderCell: (params) => (params.value ? `${Number(params.value).toLocaleString()}` : "—"),

      renderCell: (params) => (
        <span
          style={{
            direction: "rtl",
            unicodeBidi: "plaintext",
            fontWeight: "bold",
          }}
        >
          {params.value ? `${Number(params.value).toLocaleString()} ريال` : "—"}
        </span>
      ),
    },
    {
      field: "installment_months",
      headerName: "مدة السداد",
      flex: 1,
      minWidth: 120,
      sortable: false,
      headerAlign: "center",
      align: "center",

      renderCell: (params) => (
        <span
          style={{
            direction: "rtl",
            unicodeBidi: "plaintext",
            fontWeight: "bold",
          }}
        >
          {params.value ? `${params.value} شهر` : "—"}
        </span>
      ),
    },
    {
      field: "monthly_installment",
      headerName: "القسط الشهري التقريبي",
      flex: 1,
      minWidth: 140,
      sortable: false,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => (params.value ? `${Number(params.value).toLocaleString()}` : "—"),
    },
    {
      field: "total_amount",
      headerName: "الإجمالي التقديري",
      flex: 1,
      minWidth: 140,
      sortable: false,
      headerAlign: "center",
      align: "center",
      backgroundColor: "red",
      renderCell: (params) => (params.value ? `${Number(params.value).toLocaleString()}` : "—"),
    },
    {
      field: "date",
      headerName: "التاريخ",
      flex: 1,
      minWidth: 170,
      sortable: false,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => formatDate(params.value),
    },
    {
      field: "index",
      headerName: "#",
      width: 60,
      sortable: false,
      headerAlign: "center",
      align: "center",
      disableColumnMenu: true,
    },
    {
      field: "status",
      headerName: "الحالة",
      minWidth: 130,
      flex: 1,
      sortable: false,
      headerAlign: "center",
      align: "center",
      disableColumnMenu: true,
      renderCell: (params) => (
        <Chip
          disabled={updateStatusMutation.isPending}
          label={statusLabel(params.value)}
          size="small"
          onClick={() => handleStatusChange(params.row.id, params.value)}
          style={{
            ...statusColor(params.value),
            cursor: "pointer",
            fontWeight: "bold",
            fontSize: "12px",
            minWidth: "90px",
          }}
        />
      ),
    },
  ];

  const [containerWidth, setContainerWidth] = useState(window.innerWidth < 600 ? window.innerWidth - 16 : "100%");

  useEffect(() => {
    const update = () => setContainerWidth(window.innerWidth < 600 ? window.innerWidth - 16 : "100%");
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  return (
    <div className={style.container}>
      {(isPending || fetchStatus === "fetching" || updateStatusMutation.isPending) && (
        <div className={style.progressContainer}>
          <LinearProgress />
        </div>
      )}

      <div className={style.datagrid_container} style={{ width: containerWidth }}>
        <DataGrid
          rows={rows}
          columns={columns}
          getRowHeight={() => "auto"}
          getRowClassName={(params) => (params.indexRelativeToCurrentPage % 2 === 0 ? "even-row" : "odd-row")}
          initialState={{
            pagination: { paginationModel: { page: 0, pageSize: 100 } },
          }}
          pageSizeOptions={[10, 25, 50, 100]}
          checkboxSelection={false}
          disableMultipleRowSelection
          disableColumnSort
          disableMultipleColumnSorting
          disableRowSelectionOnClick
          style={{ width: "100%", height: "100%" }}
          sx={{
            // direction: "rtl",
            "& .MuiDataGrid-cell": {
              whiteSpace: "normal",
              lineHeight: "1.4",
              py: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            },

            "& .even-row": {
              backgroundColor: "#eee",
            },
            "& .odd-row": {
              backgroundColor: "#ffffff",
            },
          }}
        />
      </div>
    </div>
  );
}
